import React, { useMemo } from 'react';
import { Box, Flex, Text, SimpleGrid } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { GenderIntersex, Users, CurrencyDollar, ArrowsClockwise } from 'phosphor-react';
import { formatCurrency } from '../../../../../utils/FormatFunctions';
import { num } from '../../format';
import { IPessoalEmpregado, IPessoalResumoFolha, IPessoalHeadcount } from '../../types';
import { UI } from '../../ui';
import { InfoPopover, InfoBloco, FILTRO } from '../../components/InfoPopover';
import { classifyCargo, genderKey, JobArchetype } from './jobArchetypes';
import { statusOf } from './CollabAvatar';

interface SquadHeaderProps {
  empregados: IPessoalEmpregado[];
  folha?: IPessoalResumoFolha;
  rescisoes: number;
  serie?: IPessoalHeadcount[];
}

function Stat({
  icon,
  label,
  value,
  sub,
  color,
  info,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  color: string;
  info?: React.ReactNode;
}) {
  return (
    <Flex align="center" gap={3}>
      <Flex
        align="center"
        justify="center"
        w="42px"
        h="42px"
        borderRadius="13px"
        bgGradient={`linear(to-br, ${color}, ${color}B0)`}
        color="white"
        flexShrink={0}
        boxShadow={`0 8px 18px -6px ${color}80`}
      >
        {icon}
      </Flex>
      <Box minW={0}>
        <Flex align="center" gap={1}>
          <Text
            fontSize="10px"
            fontWeight={800}
            color={UI.faint}
            textTransform="uppercase"
            letterSpacing="0.08em"
          >
            {label}
          </Text>
          {info && <InfoPopover title={label}>{info}</InfoPopover>}
        </Flex>
        <Text fontSize="18px" fontWeight={800} color={UI.ink} lineHeight="1.1" noOfLines={1}>
          {value}
        </Text>
        {sub && (
          <Text fontSize="11px" color={UI.muted} noOfLines={1}>
            {sub}
          </Text>
        )}
      </Box>
    </Flex>
  );
}

export const SquadHeader = React.memo(function SquadHeader({
  empregados,
  folha,
  rescisoes,
  serie,
}: SquadHeaderProps) {
  const stats = useMemo(() => {
    let ativos = 0;
    let m = 0;
    let fem = 0;
    let n = 0;
    const arch = new Map<
      string,
      { label: string; accent: string; Icon: JobArchetype['Icon']; n: number }
    >();
    empregados.forEach(e => {
      if (statusOf(e.status) === 'ativo') ativos += 1;
      const g = genderKey(e.sexo);
      if (g === 'M') m += 1;
      else if (g === 'F') fem += 1;
      else n += 1;
      const a = classifyCargo(e.cargo);
      const cur = arch.get(a.key) ?? { label: a.label, accent: a.accent, Icon: a.Icon, n: 0 };
      cur.n += 1;
      arch.set(a.key, cur);
    });
    const topArch = Array.from(arch.values())
      .sort((x, y) => y.n - x.n)
      .slice(0, 6);
    return { total: empregados.length, ativos, m, fem, n, topArch };
  }, [empregados]);

  const turnover = serie && serie.length ? serie[serie.length - 1].turnover_pct : null;
  const custo = num(folha?.proventos) + num(folha?.inss_patronal) + num(folha?.fgts);
  const totalGen = Math.max(stats.m + stats.fem + stats.n, 1);
  const pctM = (stats.m / totalGen) * 100;
  const pctF = (stats.fem / totalGen) * 100;
  const pctN = (stats.n / totalGen) * 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Box
        position="relative"
        overflow="hidden"
        borderRadius="22px"
        p={{ base: 5, md: 6 }}
        mb={6}
        bg="rgba(255,255,255,0.7)"
        backdropFilter="blur(16px) saturate(160%)"
        border="1px solid rgba(255,255,255,0.85)"
        boxShadow="0 18px 50px -22px rgba(15,23,42,0.4)"
      >
        <Box
          position="absolute"
          top="-60px"
          insetEnd="-40px"
          w="240px"
          h="240px"
          borderRadius="full"
          bg={`radial-gradient(circle, ${UI.brand}28, transparent 66%)`}
          pointerEvents="none"
        />

        <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5} position="relative">
          <Stat
            icon={<Users size={22} weight="fill" />}
            label="Colaboradores"
            value={String(stats.total)}
            sub={`${stats.ativos} ativos`}
            color={UI.brand}
            info={
              <InfoBloco
                oque="Total de empregados cadastrados da empresa, com destaque para quantos estão ativos (sem data de demissão)."
                dados="extract_empregados (status, data_demissao) — quadro completo de empregados da empresa."
                filtro={FILTRO.empresaSemPeriodo}
              />
            }
          />
          <Stat
            icon={<CurrencyDollar size={22} weight="fill" />}
            label="Custo de folha"
            value={formatCurrency(custo)}
            sub="proventos + encargos"
            color={UI.pos}
            info={
              <InfoBloco
                oque="Custo total da folha no período: proventos brutos somados aos encargos patronais (INSS patronal + FGTS)."
                dados="extract_resumo_folha (proventos + inss_patronal [inss_total − inss_segurados] + fgts), somados por competência."
                filtro={FILTRO.empresa}
              />
            }
          />
          <Stat
            icon={<GenderIntersex size={22} weight="fill" />}
            label="Distribuição"
            value={`${stats.m}M · ${stats.fem}F`}
            sub={stats.n ? `${stats.n} não inf.` : 'por gênero'}
            color="#8B5CF6"
            info={
              <InfoBloco
                oque="Distribuição dos colaboradores por gênero (masculino, feminino e não informado), a partir do quadro de empregados."
                dados="extract_empregados (sexo) — contagem por gênero do quadro de empregados."
                filtro={FILTRO.empresaSemPeriodo}
              />
            }
          />
          <Stat
            icon={<ArrowsClockwise size={22} weight="fill" />}
            label="Turnover (mês)"
            value={turnover === null ? '—' : `${turnover}%`}
            sub={`${rescisoes} rescisões no período`}
            color={UI.warn}
            info={
              <InfoBloco
                oque="Rotatividade do último mês da série: média de admissões e demissões dividida pelo headcount do mês. O subtítulo mostra as rescisões no período."
                dados="extract_empregados (admissao, data_demissao) para headcount/turnover; extract_rescisoes (demissao) para a contagem de rescisões."
                filtro={FILTRO.empresa}
              />
            }
          />
        </SimpleGrid>

        {/* barra de gênero */}
        <Flex mt={5} h="8px" borderRadius="full" overflow="hidden" position="relative">
          <Box w={`${pctM}%`} bg="#3B82F6" />
          <Box w={`${pctF}%`} bg="#EC4899" />
          <Box w={`${pctN}%`} bg="#CBD5E1" />
        </Flex>

        {/* chips dos arquétipos */}
        <Flex mt={4} gap={2} wrap="wrap" position="relative">
          {stats.topArch.map(a => {
            const { Icon } = a;
            return (
              <Flex
                key={a.label}
                align="center"
                gap={1.5}
                px={2.5}
                py={1}
                borderRadius="full"
                bg={`${a.accent}14`}
                color={a.accent}
                border={`1px solid ${a.accent}2A`}
              >
                <Icon size={14} weight="fill" />
                <Text fontSize="11.5px" fontWeight={700}>
                  {a.label}
                </Text>
                <Text fontSize="11.5px" fontWeight={800}>
                  {a.n}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      </Box>
    </motion.div>
  );
});
